import type { MentionsInputStyle } from 'react-mentions';

/** Jedyna wzmianka w czacie wewnętrznym — treść w value to dosłownie `@gemma` (markup `@__display__`). */
export const GEMMA_MENTION_ENTRIES: { id: string; display: string }[] = [
  { id: 'gemma', display: 'gemma' },
];

/**
 * Jawny regex dla Mention: domyślny markupToRegex(@__display__) używa +? i dopasowuje minimalny
 * fragment po „@” (np. @g), przez co highlighter i mapowanie karetki psują się przy ręcznym „@gemma”.
 * Flagi RegExp są w tej bibliotece zabronione — „case insensitive” przez klasy znaków.
 */
export const GEMMA_MENTION_MARKUP_REGEX = /@([Gg][eE][mM][mM][aA])\b/;

/** Tekst widoczny w polu (jak getPlainText react-mentions dla wzmianki @gemma). */
export function internalChatComposerPlainText(markupValue: string): string {
  if (!markupValue) return '';
  /* Bez flagi `g` RegExp#exec przy każdym wywołaniu zwraca ten sam pierwszy hit — pętla while jest nieskończona
   * i dopina `@gemma` w kółko aż do RangeError: Invalid string length. */
  const re = new RegExp(GEMMA_MENTION_MARKUP_REGEX.source, 'g');
  return markupValue.replace(re, (_full, display: string) => `@${display}`);
}

const GEMMA_DISPLAY = 'gemma';

/**
 * Czy na końcu ostatniej linii użytkownik wpisuje @… będące prefiksem „gemma” (bez pełnej wzmianki w tej linii).
 * Używane do podpowiedzi wizualnej: wpisane znaki + jaśniejszy sufiks (np. @g + emma).
 */
export function getGemmaMentionTypingHint(markupValue: string): { query: string; suffix: string } | null {
  if (!markupValue) return null;
  const line = (markupValue.split('\n').pop() ?? '').replace(/\r$/, '');
  if (GEMMA_MENTION_MARKUP_REGEX.test(line)) return null;
  const m = line.match(/@([a-zA-Z]*)$/);
  if (!m) return null;
  const rawQ = m[1];
  const q = rawQ.toLowerCase();
  const target = GEMMA_DISPLAY.toLowerCase();
  if (!target.startsWith(q) || q.length >= target.length) return null;
  return { query: rawQ, suffix: GEMMA_DISPLAY.slice(q.length) };
}

/** Callback do react-mentions — stabilny wynik także przy pustym query (np. zaraz po wpisaniu „@”). */
export function fetchGemmaMentionData(
  query: string,
  callback: (data: { id: string; display: string }[]) => void
): void {
  const q = (query || '').trim().toLowerCase();
  let list = !q
    ? [...GEMMA_MENTION_ENTRIES]
    : GEMMA_MENTION_ENTRIES.filter((e) => e.display.toLowerCase().includes(q));
  if (!list.length) list = [...GEMMA_MENTION_ENTRIES];
  // Prefiks (np. „ge” → gemma) na górze listy
  if (q) {
    list.sort((a, b) => {
      const da = a.display.toLowerCase();
      const db = b.display.toLowerCase();
      const pa = da.startsWith(q) ? 0 : 1;
      const pb = db.startsWith(q) ? 0 : 1;
      if (pa !== pb) return pa - pb;
      return da.localeCompare(db);
    });
  }
  callback(list);
}

/** Wspólna typografia pola / highlightera / „ducha” — line-height w px, bo textarea inaczej liczy unitless 1.43. */
export const ICC_COMPOSER_FONT_PX = 14;
export const ICC_COMPOSER_LINE_HEIGHT_PX = Number((ICC_COMPOSER_FONT_PX * 1.43).toFixed(2));
export const ICC_COMPOSER_FONT_STACK = 'Roboto, Helvetica, Arial, sans-serif';

/** Ten sam inset co textarea / highlighter — warstwa podpowiedzi Gemmy musi go powielić, żeby sufiks był w jednej linii. */
export const ICC_COMPOSER_INPUT_PADDING = '8px 4px 8px 0';

/** Style pod react-mentions (drawer / jasne tło jak InputBase w InternalChatPanel). */
export const internalChatMentionsStyles: MentionsInputStyle = {
  control: {
    backgroundColor: 'transparent',
    fontSize: ICC_COMPOSER_FONT_PX,
    width: '100%',
    minHeight: 56,
    fontFamily: ICC_COMPOSER_FONT_STACK,
    lineHeight: `${ICC_COMPOSER_LINE_HEIGHT_PX}px`,
  },
  highlighter: {
    padding: ICC_COMPOSER_INPUT_PADDING,
    border: '1px solid transparent',
    minHeight: 56,
    /* Ukrywa tylko poziomy „zlew” — pionowy scroll jak w textarea (react-mentions synchronizuje scrollTop). */
    overflowX: 'hidden',
    overflowY: 'auto',
    fontSize: ICC_COMPOSER_FONT_PX,
    lineHeight: `${ICC_COMPOSER_LINE_HEIGHT_PX}px`,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    color: 'transparent',
  },
  input: {
    padding: ICC_COMPOSER_INPUT_PADDING,
    margin: 0,
    border: 'none',
    outline: 'none',
    minHeight: 56,
    fontSize: ICC_COMPOSER_FONT_PX,
    lineHeight: `${ICC_COMPOSER_LINE_HEIGHT_PX}px`,
    resize: 'none',
    overflow: 'auto',
    color: '#020242',
    backgroundColor: 'transparent',
    width: '100%',
    boxSizing: 'border-box',
  },
  suggestions: {
    /* Nad MuiDrawer / Modal (~1200–1300); domyślnie react-mentions ma tu zIndex: 1 — lista była niewidoczna. */
    zIndex: 2000,
    list: {
      backgroundColor: '#fff',
      border: '1px solid #e8e8ef',
      borderRadius: 8,
      boxShadow: '0 8px 28px rgba(0,0,0,0.14)',
      fontSize: 14,
      maxHeight: 220,
      overflow: 'auto',
    },
    item: {
      padding: '10px 12px',
      borderBottom: '1px solid #f2f2f7',
      '&focused': {
        backgroundColor: '#E8E6FF',
      },
    },
  },
};
